import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import {
  WorkbenchSearchInput,
  type WorkbenchSearchInputProps,
} from "./WorkbenchSearchInput";
import {
  WORKBENCH_ROOT_PADDING,
  clampMainWidth,
  measureMainMinWidth,
  resolveMinMainWidth,
} from "./workbenchLayout";
import { WORKBENCH_SURFACE_CLASS, WORKBENCH_ROOT_TEXT_CLASS, WORKBENCH_TOOLBAR_DIVIDER_CLASS } from "./workbenchSurface";

export interface WorkbenchTab {
  id: string;
  label: string;
}

export interface WorkbenchSearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  accessory?: WorkbenchSearchInputProps["accessory"];
}

export interface WorkbenchProps {
  /** Top-level view tabs (left of the toolbar). */
  tabs: WorkbenchTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  tabTestIdPrefix?: string;
  /** Inline search pill in the toolbar (optional; Guardians uses hover overlay instead). */
  search?: WorkbenchSearchConfig;
  /** Center toolbar slot (e.g. active filter chips). */
  toolbarCenter?: ReactNode;
  /** Hover-activated search overlay over ``toolbarCenter`` (opened from the search trigger only). */
  searchHoverOpen?: boolean;
  searchHover?: WorkbenchSearchConfig;
  /** Keep the overlay open while the pointer is over the expanded search field. */
  onSearchHoverPointerEnter?: () => void;
  onSearchHoverPointerLeave?: () => void;
  /** Right-aligned toolbar controls (filters, links, settings, status…). */
  actions?: ReactNode;
  /** Optional second toolbar row for view-specific controls. */
  secondary?: ReactNode;
  /** Main list/content area. */
  children: ReactNode;
  /** One or more detail panels rendered to the right of the main column. */
  panels?: ReactNode;
  /** Width class for the main column when panels are open. */
  mainColumnClassName?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * Generic list workbench: a tabbed, searchable, filterable main column paired
 * with one or more detail side panels. Shared by the Guardians dashboard and a
 * migration target for Mission Control.
 *
 * Layout contract: the primary toolbar row keeps a fixed left-to-right order —
 * tabs, search, actions — so chrome stays identical across tabs. Per-view
 * controls live on the optional `secondary` row. Detail panels are passed in by
 * the caller (typically `SidePanel`s) and laid out as flex siblings, enabling
 * the "right panel opens, then the left list changes, then the right updates"
 * cross-navigation flow. When panels are open a draggable separator lets the
 * user rebalance the main list against the panels (both sides stay live).
 */
export function Workbench({
  tabs,
  activeTab,
  onTabChange,
  tabTestIdPrefix = "workbench-tab",
  search,
  toolbarCenter,
  searchHoverOpen = false,
  searchHover,
  onSearchHoverPointerEnter,
  onSearchHoverPointerLeave,
  actions,
  secondary,
  children,
  panels,
  mainColumnClassName,
  className,
  "data-testid": testId = "workbench",
}: WorkbenchProps) {
  const hasPanels = Boolean(panels);

  const containerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const draggingRef = useRef(false);
  const [mainWidth, setMainWidth] = useState<number | null>(null);
  const [minMainWidth, setMinMainWidth] = useState(resolveMinMainWidth(0));

  const clampWidth = useCallback(
    (width: number) => {
      const container = containerRef.current;
      if (!container) return width;
      const inner = container.clientWidth - WORKBENCH_ROOT_PADDING * 2;
      return clampMainWidth(width, {
        containerInnerWidth: inner,
        minMainWidth,
      });
    },
    [minMainWidth]
  );

  const syncMinMainWidth = useCallback(() => {
    const main = mainRef.current;
    if (!main || !hasPanels) return;
    const nextMin = resolveMinMainWidth(measureMainMinWidth(main));
    setMinMainWidth((prev) => (prev === nextMin ? prev : nextMin));
    setMainWidth((w) => (w == null ? w : clampMainWidth(w, {
      containerInnerWidth:
        (containerRef.current?.clientWidth ?? 0) - WORKBENCH_ROOT_PADDING * 2,
      minMainWidth: nextMin,
    })));
  }, [hasPanels]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!draggingRef.current) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left - WORKBENCH_ROOT_PADDING;
      setMainWidth(clampWidth(x));
    },
    [clampWidth]
  );

  const stopDragging = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  useEffect(() => {
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDragging);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
    };
  }, [handlePointerMove, stopDragging]);

  // Toolbar chrome (tabs, filter chips, optional slot actions) sets the floor.
  useLayoutEffect(() => {
    if (!hasPanels) return;
    syncMinMainWidth();
  }, [
    hasPanels,
    syncMinMainWidth,
    tabs,
    activeTab,
    actions,
    toolbarCenter,
    secondary,
    search,
    searchHoverOpen,
  ]);

  useEffect(() => {
    if (!hasPanels) return;
    const header = headerRef.current;
    if (!header || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => syncMinMainWidth());
    observer.observe(header);
    return () => observer.disconnect();
  }, [hasPanels, syncMinMainWidth]);

  // Keep a manual width valid when the window / container resizes.
  useEffect(() => {
    if (!hasPanels) return;
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setMainWidth((w) => (w == null ? w : clampWidth(w)));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [hasPanels, clampWidth]);

  const startDragging = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      if (mainWidth == null && mainRef.current) {
        setMainWidth(clampWidth(mainRef.current.offsetWidth));
      }
      draggingRef.current = true;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    },
    [clampWidth, mainWidth]
  );

  const useInlineWidth = hasPanels && mainWidth != null;

  return (
    <div
      ref={containerRef}
      className={clsx("relative flex h-full w-full p-4", WORKBENCH_ROOT_TEXT_CLASS, className)}
      data-testid={testId}
    >
      <main
        ref={mainRef}
        style={{
          ...(useInlineWidth ? { width: mainWidth, flexGrow: 0, flexShrink: 0 } : null),
          // Keep the compressed toolbar floor valid even before the first drag
          // (e.g. deep-linking straight into a list with a panel already open).
          ...(hasPanels ? { minWidth: minMainWidth } : null),
        }}
        className={clsx(
          "flex min-w-0 flex-col rounded-2xl px-5 py-4",
          WORKBENCH_SURFACE_CLASS,
          !useInlineWidth && (mainColumnClassName ?? "flex-1")
        )}
      >
        <header
          ref={headerRef}
          className={clsx("mb-3 shrink-0 space-y-2 pb-2", WORKBENCH_TOOLBAR_DIVIDER_CLASS)}
        >
          <div
            className="flex min-w-0 items-center gap-2"
            data-workbench-toolbar-row
          >
            <div data-workbench-toolbar-tabs className="shrink-0">
              <SegmentedTabs
                tabs={tabs}
                activeId={activeTab}
                showDivider={false}
                tabTestIdPrefix={tabTestIdPrefix}
                onChange={onTabChange}
              />
            </div>
            {search ? (
              <WorkbenchSearchInput
                value={search.value}
                onChange={search.onChange}
                placeholder={search.placeholder}
                accessory={search.accessory}
                className="min-w-[160px] flex-1"
              />
            ) : toolbarCenter ? (
              <div
                className="relative flex min-w-0 flex-auto items-center"
                data-testid="workbench-toolbar-center"
              >
                {toolbarCenter}
                {searchHoverOpen && searchHover ? (
                  <div
                    className="absolute right-0 top-0 bottom-0 z-20 flex w-72 max-w-full origin-right animate-in items-center overflow-hidden rounded-xl border border-slate-500 bg-slate-800 shadow-[0_8px_24px_rgba(0,0,0,0.45)] ring-1 ring-white/10 duration-150 ease-out fade-in zoom-in-95 slide-in-from-right-1 motion-reduce:animate-none"
                    data-testid="workbench-search-hover"
                    onPointerEnter={onSearchHoverPointerEnter}
                    onPointerLeave={onSearchHoverPointerLeave}
                  >
                    <WorkbenchSearchInput
                      value={searchHover.value}
                      onChange={searchHover.onChange}
                      placeholder={searchHover.placeholder}
                      accessory={searchHover.accessory}
                      embedded
                      showLeadingIcon
                      className="h-full w-full"
                    />
                  </div>
                ) : null}
              </div>
            ) : searchHover ? (
              <div
                className="relative flex min-w-0 flex-1 items-center"
                data-testid="workbench-toolbar-center"
              >
                {searchHoverOpen && searchHover ? (
                  <div
                    className="absolute inset-0 z-20 flex origin-right animate-in items-center overflow-hidden rounded-xl border border-slate-500 bg-slate-800 shadow-[0_8px_24px_rgba(0,0,0,0.45)] ring-1 ring-white/10 duration-150 ease-out fade-in zoom-in-95 slide-in-from-right-1 motion-reduce:animate-none"
                    data-testid="workbench-search-hover"
                    onPointerEnter={onSearchHoverPointerEnter}
                    onPointerLeave={onSearchHoverPointerLeave}
                  >
                    <WorkbenchSearchInput
                      value={searchHover.value}
                      onChange={searchHover.onChange}
                      placeholder={searchHover.placeholder}
                      accessory={searchHover.accessory}
                      embedded
                      showLeadingIcon
                      className="h-full w-full"
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="flex-1" />
            )}
            {actions ? (
              <div
                data-workbench-toolbar-actions
                className="flex min-w-0 shrink items-center gap-2"
              >
                {actions}
              </div>
            ) : null}
          </div>
          {secondary ? (
            <div className="flex items-center gap-2 overflow-x-auto">{secondary}</div>
          ) : null}
        </header>

        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </main>

      {hasPanels ? (
        <>
          <div
            role="separator"
            aria-orientation="vertical"
            onPointerDown={startDragging}
            data-testid="workbench-resizer"
            className="w-4 shrink-0 cursor-col-resize touch-none"
          />
          <div className="flex min-w-0 flex-1 gap-4">{panels}</div>
        </>
      ) : null}
    </div>
  );
}
